import { useState, useEffect } from "react";
import { MoveLeft } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { Dropdown } from "react-bootstrap";
import { getTripCostViewApi, deleteTripsApi } from "./trip-cost-api";
import locationIcon from "../../assets/svg/location_ic.svg";
import moreIcon from "../../assets/svg/more_ic.svg";

import DeleteModal from "../common/deleteModel";
import dateIc from "../../assets/svg/date_ic.svg";
import { downloadBlobFile, sanitizeInput } from "../common/security-utils";
import CommonSnackbar from "../common/reusable-commponent";

import tripIcon from "../../assets/svg/trip_view_ic.svg";
import milestoneIcon from "../../assets/svg/flag_ic.svg";
import distanceIcon from "../../assets/png/estimated_km_so.png";
import timestampIcon from "../../assets/svg/time_ic.svg";
import truckIcon from "../../assets/svg/truck_ic.svg";

import totalAmtIc from "../../assets/svg/rupee.svg";

import fuelCostIc from "../../assets/svg/fuel-cost-ic.svg";
import fastagIc from "../../assets/svg/fastag-ic.svg";
import otherCostIc from "../../assets/svg/other-cost.svg";
import attachment from "../../assets/png/attachment.png";
import downloadIc from "../../assets/svg/download_Ics.svg";
import "../common/skeleton-effect.css";
import "./trip-cost-create.css";
import { useGlobal } from "../../GlobalContext";

export const TripCostView = () => {
  const { rolePermissionList } = useGlobal();
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = location.state || {};

  const [viewStopDetails, setViewStopDetails] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState("Fuel Cost");

  // Snackbar state for secure error reporting
  const [snackbar, setSnackbar] = useState({ show: false, message: "", isSuccess: true });

  const showToast = (message, isSuccess = true) => {
    setSnackbar({ show: true, message, isSuccess });
  };

  useEffect(() => {
    if (id != null) {
      const sanitizedId = sanitizeInput(String(id));
      if (sanitizedId) {
        getTripCostView(sanitizedId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  /**
   * VAPT Compliance: Permission check hardened against prototype pollution
   */
  const checkPermission = (permission) => {
    if (!rolePermissionList || !Array.isArray(rolePermissionList)) return false;
    return rolePermissionList.some(
      (element) => element?.name === "Trip cost" && element?.menu_permission === true && element?.[permission] === true
    );
  };

  const getTripCostView = async (tripId) => {
    setIsLoading(true);
    try {
      const response = await getTripCostViewApi(tripId);
      if (response?.data?.success === true) {
        setViewStopDetails(response.data.trip_cost ?? {});
      } else {
        showToast(response?.data?.message || "Failed to fetch trip cost details", false);
      }
    } catch (err) {
      // VAPT: Avoid leaking error details to the UI
      showToast("A network error occurred while fetching details", false);
    } finally {
      setIsLoading(false);
    }
  };

  const [deleteId, setDeleteId] = useState(null);
  const [isOpenDelete, setIsOpenDelete] = useState(false);

  useEffect(() => {
    if (deleteId !== null) {
      setIsOpenDelete(true);
    }
  }, [deleteId]);

  const deleteVendor = async () => {
    if (!deleteId) return;
    try {
      const response = await deleteTripsApi(deleteId);
      if (response?.data?.success === true) {
        showToast("Trip cost deleted successfully", true);
        setTimeout(() => navigate(-1), 1000);
      } else {
        showToast(response?.data?.message || "Failed to delete trip cost", false);
      }
    } catch (err) {
      showToast("A network error occurred during deletion", false);
    } finally {
      setIsOpenDelete(false);
      setDeleteId(null);
    }
  };

  const downloadFile = (fileUrl, fileName) => {
    // VAPT: Sanitized and validated via security utilities
    downloadBlobFile(fileUrl, fileName);
  };

  return (
    <>
      <DeleteModal
        title={"trip cost"}
        isOpen={isOpenDelete}
        onClose={() => {
          setIsOpenDelete(false);
          setDeleteId(null);
        }}
        onDelete={deleteVendor}
      />

      <CommonSnackbar
        show={snackbar.show}
        message={snackbar.message}
        isSuccess={snackbar.isSuccess}
        onClose={() => setSnackbar({ ...snackbar, show: false })}
      />

      <div>
        <div
          className="py-2 mt-2 bg-white d-flex align-items-center justify-content-between"
          style={{
            borderTopLeftRadius: "16px",
            borderTopRightRadius: "16px",
          }}
        >
          <div className="d-flex align-items-center justify-content-start">
            <div className="ms-2 d-flex align-items-center justify-content-center border rounded-5 bg-white" style={{ width: "36px", height: "36px" }}>
              <MoveLeft
                className="cursor-pointer"
                onClick={() => {
                  navigate(-1);
                }}
              />
            </div>

            <div
              className="ms-3 d-flex align-items-center justify-content-center"
              style={{
                width: "64px",
                height: "64px",
                borderRadius: "16px",
                background: "#DFE8FF",
                color: "#3469E7",
              }}
            >
              <img src={tripIcon} alt="tripIcon" />
            </div>
            {isLoading ? (
              <div className="ms-2 d-flex flex-column align-items-start justify-content-center gap-2">
                <div className="skeleton skeleton-text" style={{ width: "150px" }} />
                <div className="d-flex align-items-center gap-2">
                  <div className="skeleton skeleton-text-sm" style={{ width: "120px" }} />
                  <div className="skeleton" style={{ width: "80px", height: "24px", borderRadius: "8px" }} />
                </div>
              </div>
            ) : (
              <div className="ms-2 d-flex flex-column align-items-start justify-content-center gap-1">
                <div className="fs-20 fw-600 textFontColor">{viewStopDetails.trip_code ?? ""}</div>
                <div className="d-flex align-items-center justify-content-start">
                  <img src={locationIcon} alt="location" />
                  <span className="ms-2 fs-13 fw-400 " style={{ color: "#A2A7AE" }}>
                    {viewStopDetails.location_name ?? ""}
                  </span>
                  <div
                    className="mx-2"
                    style={{
                      width: "5px",
                      height: "5px",
                      borderRadius: "100px",
                      background: "#D9D9D9",
                    }}
                  ></div>
                  <div className="d-flex align-items-center justify-content-center rounded-4 px-2 py-1" style={{ background: "#EDF2FF", color: "#3469E7" }}>
                    <span className="fs-12 fw-500">#{viewStopDetails.mile_stone ?? ""}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="d-flex align-items-center justify-content-start me-2">
            <div className="ms-2">
              {(checkPermission("edit_permission") || checkPermission("delete_permission")) && (
                <Dropdown>
                  <Dropdown.Toggle
                    bsPrefix="custom-toggle"
                    variant="light"
                    id="dropdown-sort"
                    className="d-flex align-items-center justify-content-between rounded-5 bg-white"
                    style={{
                      width: "36px",
                      height: "36px",
                      border: "1px solid #EBEBEB",
                    }}
                  >
                    <span className="d-flex align-items-center px-1 gap-2">
                      <img src={moreIcon} alt="more" />
                    </span>
                  </Dropdown.Toggle>

                  <Dropdown.Menu
                    style={{
                      backgroundColor: "white",
                      borderRadius: "8px",
                      border: "1px solid #EBEBEB",
                      boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                    }}
                  >
                    {checkPermission("edit_permission") && (
                      <Dropdown.Item
                        className="fs-14 fw-500 textcolor cursor-pointer"
                        onClick={() => {
                          navigate("/cost/create", {
                            state: { id: sanitizeInput(String(id)) },
                          });
                        }}
                      >
                        Edit
                      </Dropdown.Item>
                    )}
                    {checkPermission("delete_permission") && (
                      <Dropdown.Item
                        className="fs-14 fw-500 textcolor cursor-pointer"
                        onClick={() => {
                          setDeleteId(sanitizeInput(String(id)));
                        }}
                      >
                        Delete
                      </Dropdown.Item>
                    )}
                  </Dropdown.Menu>
                </Dropdown>
              )}
            </div>
          </div>
        </div>

        {isLoading ? (
          <>
            <div className="row d-flex align-items-stretch">
              <div className="col-12 col-lg-6 mt-2">
                <div className="bg-white rounded-4 p-3 d-flex flex-column align-items-start justify-content-start">
                  <div className="skeleton skeleton-text-lg mb-3" style={{ width: "120px" }} />
                  {Array.from({ length: 5 }).map((_, idx) => (
                    <div key={idx} className="mt-3 d-flex align-items-center justify-content-start w-100">
                      <div style={{ width: "200px" }} className="d-flex align-items-center justify-content-start gap-2">
                        <div className="skeleton" style={{ width: "20px", height: "20px" }} />
                        <div className="skeleton skeleton-text-sm" style={{ width: "140px" }} />
                      </div>
                      <div className="skeleton skeleton-text" style={{ width: "150px" }} />
                    </div>
                  ))}
                </div>
              </div>

              <div className="h-100 col-12 col-lg-6 mt-2">
                <div className="h-100 bg-white rounded-4 p-3 d-flex flex-column align-items-start justify-content-start">
                  <div className="skeleton skeleton-text-lg mb-3" style={{ width: "140px" }} />
                  <div className="mt-2 w-100 row d-flex align-items-stretch">
                    <div className="col-6">
                      <div className="h-100 rounded-3 p-3 d-flex flex-column align-items-center justify-content-center gap-2" style={{ background: "#F0F5FF" }}>
                        <div className="skeleton" style={{ width: "64px", height: "64px", borderRadius: "50%" }} />
                        <div className="skeleton skeleton-text" style={{ width: "100px" }} />
                        <div className="skeleton skeleton-text-sm" style={{ width: "80px" }} />
                      </div>
                    </div>
                    <div className="col-6 d-flex flex-column gap-2">
                      <div className="rounded-3 px-2 py-3" style={{ border: "1px solid #EBEBEB" }}>
                        <div className="skeleton skeleton-text-sm mb-2" style={{ width: "100%" }} />
                        <div className="skeleton skeleton-text" style={{ width: "120px" }} />
                      </div>
                      <div className="rounded-3 px-2 py-3" style={{ border: "1px solid #EBEBEB" }}>
                        <div className="skeleton skeleton-text-sm mb-2" style={{ width: "60px" }} />
                        <div className="skeleton skeleton-text" style={{ width: "100px" }} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-3 d-flex align-items-center justify-content-start gap-2">
              {Array.from({ length: 4 }).map((_, idx) => (
                <div key={idx} className="col-6 col-md-3 rounded-3 p-2 d-flex align-items-center justify-content-start" style={{ background: "#FFFFFF" }}>
                  <div className="skeleton" style={{ width: "28px", height: "28px", borderRadius: "50%" }} />
                  <div className="ms-2 flex-grow-1">
                    <div className="skeleton skeleton-text-sm mb-2" style={{ width: "80px" }} />
                    <div className="skeleton skeleton-text" style={{ width: "100px" }} />
                  </div>
                </div>
              ))}
            </div>

            <div className="m-2 bg-white rounded-4 p-3">
              <div className="d-flex gap-2 mb-3">
                {Array.from({ length: 3 }).map((_, idx) => (
                  <div key={idx} className="skeleton skeleton-tab" />
                ))}
              </div>
              <div className="mt-3">
                <div className="skeleton skeleton-table-header mb-2" />
                {Array.from({ length: 3 }).map((_, idx) => (
                  <div key={idx} className="skeleton skeleton-table-row" />
                ))}
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="row d-flex align-items-stretch">
              <div className="col-12 col-lg-6 mt-2">
                <div className="bg-white rounded-4 p-3 d-flex flex-column align-items-start justify-content-start">
                  <div className="fs-16 fw-600 textFontColor">Trip details</div>

                  <div className="mt-3 d-flex align-items-center justify-content-start">
                    <div style={{ width: "200px" }} className="d-flex align-items-center justify-content-start">
                      <img src={milestoneIcon} alt="location" />
                      <span className="ms-2 fs-13 fw-500 cardTextGrey">Milestone group</span>
                    </div>
                    <div className="fs-13 fw-500 textFontColor">{viewStopDetails.mile_stone ?? ""}</div>
                  </div>

                  <div className="mt-3 d-flex align-items-center justify-content-start">
                    <div style={{ width: "200px" }} className="d-flex align-items-center justify-content-start">
                      <img src={dateIc} alt="date" />
                      <span className="ms-2 fs-13 fw-500 cardTextGrey">Actual delivery date</span>
                    </div>
                    <div className="fs-13 fw-500 textFontColor">{viewStopDetails.act_date ?? ""}</div>
                  </div>

                  <div className="mt-3 d-flex align-items-center justify-content-start">
                    <div style={{ width: "200px" }} className="d-flex align-items-center justify-content-start">
                      <img src={distanceIcon} alt="date" />
                      <span className="ms-2 fs-13 fw-500 cardTextGrey">Estimated KM</span>
                    </div>
                    <div className="fs-13 fw-500 textFontColor">{viewStopDetails.est_km ?? ""}</div>
                  </div>

                  <div className="mt-3 d-flex align-items-center justify-content-start">
                    <div style={{ width: "200px" }} className="d-flex align-items-center justify-content-start">
                      <img src={distanceIcon} alt="date" />
                      <span className="ms-2 fs-13 fw-500 cardTextGrey">Actual KM</span>
                    </div>
                    <div className="fs-13 fw-500 textFontColor">{viewStopDetails.act_km ?? ""}</div>
                  </div>

                  <div className="mt-3 d-flex align-items-center justify-content-start">
                    <div style={{ width: "200px" }} className="d-flex align-items-center justify-content-start">
                      <img src={timestampIcon} alt="date" />
                      <span className="ms-2 fs-13 fw-500 cardTextGrey">Last tracking on</span>
                    </div>
                    <div className="fs-13 fw-500 textFontColor">
                      {viewStopDetails.update_date ?? ""} {" - "}
                      {viewStopDetails.update_time ?? ""}
                    </div>
                  </div>
                </div>
              </div>

              <div className="h-100 col-12 col-lg-6 mt-2">
                <div className="h-100 bg-white rounded-4 p-3 d-flex flex-column align-items-start justify-content-start">
                  <div className="fs-16 fw-600 textFontColor">Vehicle details</div>
                  <div className="mt-2 w-100 row d-flex align-items-stretch">
                    <div className="col-6">
                      <div className="h-100 rounded-3 p-3 d-flex flex-column align-items-center justify-content-center" style={{ background: "#F0F5FF" }}>
                        <div
                          style={{
                            width: "64px",
                            height: "64px",
                            borderRadius: "100px",
                          }}
                        >
                          <img src={viewStopDetails.vehicle_image ?? truckIcon} alt="vehicle" style={{ width: "64px", height: "64px" }} />
                        </div>
                        <div className="fs-20 fw-700" style={{ color: "#28303F" }}>
                          {viewStopDetails.license_plate ?? ""}
                        </div>
                        <div className="fs-13 fw-500" style={{ color: "#3469E7" }}>
                          {viewStopDetails.vehicle_name ?? ""}
                        </div>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="rounded-3 px-2 py-3" style={{ border: "1px solid #EBEBEB" }}>
                        <div className="fs-11 fw-500" style={{ color: "#626F86", textAlign: "start" }}>
                          VEHICLE IDENTIFICATION NUMBER
                        </div>
                        <div className="fs-14 fw-500" style={{ color: "#172B4D", textAlign: "start" }}>
                          {viewStopDetails.vin ?? ""}
                        </div>
                      </div>

                      <div className="mt-2 rounded-3 px-2 py-3" style={{ border: "1px solid #EBEBEB" }}>
                        <div className="fs-11 fw-500" style={{ color: "#626F86", textAlign: "start" }}>
                          VENDOR
                        </div>
                        <div className="fs-14 fw-500" style={{ color: "#172B4D", textAlign: "start" }}>
                          {viewStopDetails.vendor_name ?? ""}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-2 row px-2">
              <div className="col-3 px-1">
                <div className="rounded-3 p-2 d-flex align-items-center justify-content-start" style={{ background: "#FFFFFF" }}>
                  <div
                    style={{
                      width: "28px",
                      height: "28px",
                      background: "#FF4444",
                      borderRadius: "100px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img src={totalAmtIc} alt="total cost" />
                  </div>
                  <div className="ms-2">
                    <div className="fs-12 fw-400" style={{ color: "#8790A1", textAlign: "start" }}>
                      Total Cost
                    </div>
                    <div className="fs-16 fw-600 mt-1" style={{ color: "#172B4D", textAlign: "start" }}>
                      {viewStopDetails.total_cost ?? ""}
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-3 px-1">
                <div className="rounded-3 p-2 d-flex align-items-center justify-content-start" style={{ background: "#FFFFFF" }}>
                  <div
                    style={{
                      width: "28px",
                      height: "28px",
                      background: "#454CF2",
                      borderRadius: "100px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img src={fuelCostIc} alt="fuelCostIc" />
                  </div>
                  <div className="ms-2">
                    <div className="fs-12 fw-400" style={{ color: "#8790A1", textAlign: "start" }}>
                      Fuel Cost
                    </div>
                    <div className="fs-16 fw-600 mt-1" style={{ color: "#172B4D", textAlign: "start" }}>
                      {viewStopDetails.fual_total ?? ""}
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-3 px-1">
                <div className="rounded-3 p-2 d-flex align-items-center justify-content-start" style={{ background: "#FFFFFF" }}>
                  <div
                    style={{
                      width: "28px",
                      height: "28px",
                      background: "#2C8C53",
                      borderRadius: "100px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img src={fastagIc} alt="fastagIc" />
                  </div>
                  <div className="ms-2">
                    <div className="fs-12 fw-400" style={{ color: "#8790A1", textAlign: "start" }}>
                      Fastag Cost
                    </div>
                    <div className="fs-16 fw-600 mt-1" style={{ color: "#172B4D", textAlign: "start" }}>
                      {viewStopDetails.fast_tag_total ?? ""}
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-3 px-1">
                <div className="rounded-3 p-2 d-flex align-items-center justify-content-start" style={{ background: "#FFFFFF" }}>
                  <div
                    style={{
                      width: "28px",
                      height: "28px",
                      background: "#FDCA01",
                      borderRadius: "100px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img src={otherCostIc} alt="otherCostIc" />
                  </div>
                  <div className="ms-2">
                    <div className="fs-12 fw-400" style={{ color: "#8790A1", textAlign: "start" }}>
                      Other Cost
                    </div>
                    <div className="fs-16 fw-600 mt-1" style={{ color: "#172B4D", textAlign: "start" }}>
                      {viewStopDetails.other_total ?? ""}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="m-2 bg-white rounded-4 p-3 mx-0">
              <div className="row">
                <div className="col-12 ">
                  <div className="row">
                    <div className="tab-nav-wrapper mt-2">
                      <div
                        className={`tab-button ${selectedTab === "Fuel Cost" ? "active" : "inactive"}`}
                        onClick={() => {
                          setSelectedTab("Fuel Cost");
                        }}
                      >
                        Fuel Cost
                      </div>
                      <div
                        className={`tab-button ${selectedTab === "Fastag Cost" ? "active" : "inactive"}`}
                        onClick={() => {
                          setSelectedTab("Fastag Cost");
                        }}
                      >
                        Fastag Cost
                      </div>
                      <div
                        className={`tab-button ${selectedTab === "Other Cost" ? "active" : "inactive"}`}
                        onClick={() => {
                          setSelectedTab("Other Cost");
                        }}
                      >
                        Other Cost
                      </div>
                    </div>

                    {selectedTab === "Fuel Cost" && (
                      <div className="cost-table-container">
                        <div className="cost-table-wrapper">
                          <table className="cost-table-root">
                            <thead className="cost-table-thead">
                              <tr>
                                <th>Fuel Station</th>
                                <th>Location</th>
                                <th>Quantity</th>
                                <th>Price per Liter</th>
                                <th>Total Price</th>
                              </tr>
                            </thead>
                            <tbody>
                              {viewStopDetails.fual_cost?.length > 0 ? (
                                viewStopDetails.fual_cost.map((row) => (
                                  <tr key={row?.id || Math.random()} className="cost-table-row">
                                    <td className="cost-table-cell fw-500">{row.fuelbank || "-"}</td>
                                    <td className="cost-table-cell">{row.location || "-"}</td>
                                    <td className="cost-table-cell">{row.quentity || "0"}</td>
                                    <td className="cost-table-cell">₹{row.unit_price || "0"}</td>
                                    <td className="cost-table-cell fw-600">₹{row.totalprice || "0"}</td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan="5" className="text-center py-4 text-muted">No fuel cost entries found</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {selectedTab === "Fastag Cost" && (
                      <div className="cost-table-container">
                        <div className="cost-table-wrapper">
                          <table className="cost-table-root">
                            <thead className="cost-table-thead">
                              <tr>
                                <th>Location</th>
                                <th>Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {viewStopDetails.fast_tag_cost?.length > 0 ? (
                                viewStopDetails.fast_tag_cost.map((row) => (
                                  <tr key={row?.id || Math.random()} className="cost-table-row">
                                    <td className="cost-table-cell">{row.location || "-"}</td>
                                    <td className="cost-table-cell fw-600">₹{row.amount || "0"}</td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan="2" className="text-center py-4 text-muted">No Fastag entries found</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {selectedTab === "Other Cost" && (
                      <div className="cost-table-container">
                        <div className="cost-table-wrapper">
                          <table className="cost-table-root">
                            <thead className="cost-table-thead">
                              <tr>
                                <th>Description</th>
                                <th>Cost</th>
                              </tr>
                            </thead>
                            <tbody>
                              {viewStopDetails.other_cost?.length > 0 ? (
                                viewStopDetails.other_cost.map((row) => (
                                  <tr key={row?.id || Math.random()} className="cost-table-row">
                                    <td className="cost-table-cell">{row.description || "-"}</td>
                                    <td className="cost-table-cell fw-600">₹{row.cost || "0"}</td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan="2" className="text-center py-4 text-muted">No other cost entries found</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="m-2 bg-white rounded-4 p-3 mx-0">
              <p className="fs-16 fw-600" style={{ textAlign: "start" }}>
                Attachment
              </p>
              <div className="row gap-2">
                {(viewStopDetails?.attachments || []).map((file, index) => (
                  <div key={index} className="col-12 col-md-4 col-lg-3 rounded-4 p-2 d-flex align-items-center justify-content-between gap-3 my-2" style={{ backgroundColor: "#F8F8F8" }}>
                    <div className="d-flex align-items-center justify-content-start gap-2">
                      <img src={attachment} alt="pdf" style={{ width: "32px", height: "32px" }} />
                      <div className="d-flex flex-column">
                        <p className="m-0 fs-13 fw-500">{file?.file_name || "-"}</p>
                        <p className="m-0 fs-12 fw-400">{file?.file_size || "0 "} KB</p>
                      </div>
                    </div>
                    {file.file_path && (
                      <div
                        className="cursor-pointer d-flex align-items-center justify-content-center"
                        onClick={() => downloadFile(file.file_path, file.file_path.split("/").pop() || "download")}
                        style={{
                          width: "32px",
                          height: "32px",
                          borderRadius: "100px",
                          background: "#EBEBEB"
                        }}
                      >
                        <img
                          src={downloadIc}
                          alt="download"
                          style={{
                            width: "12.8px",
                            height: "12px",
                          }}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};
